// Editorial copy + slugs for the /blog/category/:slug index pages.
//
// Categories themselves live in blogPosts.ts (one-word strings). This
// module adds:
//  - A URL-safe slug
//  - A page title + meta description used by prerender + the page header
//  - A short intro paragraph rendered above the post grid
//
// If you add a new category in blogPosts.ts, add an entry here too —
// missing entries fall back to a generic title.

import { CATEGORIES as RAW_CATEGORIES } from './blogPosts';

export interface BlogCategory {
  slug: string;
  /** Original category string as written on BlogPost.category */
  name: string;
  /** Long-form display title used in the page header */
  displayTitle: string;
  /** Used for <meta description> and the page intro */
  description: string;
  /** Emoji shown on the OG card and page header */
  icon: string;
}

export function categorySlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

const COPY: Record<string, Pick<BlogCategory, 'displayTitle' | 'description' | 'icon'>> = {
  Updates: {
    displayTitle: 'Updates',
    icon: '📢',
    description:
      "Product updates, new game modes, leaderboard changes, and the occasional behind-the-scenes note from the team building EarthGuessr.",
  },
  Geography: {
    displayTitle: 'Geography',
    icon: '🌍',
    description:
      "Deep dives into how to read the planet — satellite-imagery cues, country-by-country signatures, climate and agricultural patterns, and the geography questions players actually ask.",
  },
  'Behind the Scenes': {
    displayTitle: 'Behind the scenes',
    icon: '🔧',
    description:
      "How EarthGuessr gets built — engineering notes, data pipelines, mapping infrastructure, and the technical decisions behind the world's satellite-imagery geography game.",
  },
  Community: {
    displayTitle: 'Community',
    icon: '👥',
    description:
      "Posts about the EarthGuessr community — competitive players, school groups, multiplayer lobbies, and the people who make the game more fun than the team could on its own.",
  },
  'Team Building': {
    displayTitle: 'Team building',
    icon: '🤝',
    description:
      "EarthGuessr as a workplace ritual — virtual icebreakers, remote-team activities, and how organisations use the game for connection and shared learning.",
  },
  Education: {
    displayTitle: 'Education',
    icon: '🎓',
    description:
      "Classroom resources and teaching ideas — how teachers use EarthGuessr in geography lessons, multiplayer lobbies for classes, and lesson-ready guides for students.",
  },
};

export const BLOG_CATEGORIES: BlogCategory[] = RAW_CATEGORIES
  .filter((c) => c !== 'All')
  .map((name) => {
    const copy = COPY[name];
    return {
      slug: categorySlug(name),
      name,
      displayTitle: copy?.displayTitle || name,
      description: copy?.description || `Posts in the ${name} category on the EarthGuessr blog.`,
      icon: copy?.icon || '📝',
    };
  });

export function blogCategoryBySlug(slug: string): BlogCategory | undefined {
  return BLOG_CATEGORIES.find((c) => c.slug === slug);
}
